// src/Calculators/CommonComponents/Header.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, ChevronDown, User } from "lucide-react";
import 'bootstrap/dist/css/bootstrap.min.css';
import './Navbar.css';

const Navbar = ({ toggleSidebar, toggleAdminSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // Helper function to get token from either storage
  const getToken = () => {
    return sessionStorage.getItem("token") || localStorage.getItem("token");
  };

  // Helper function to get userData from either storage
  const getUserData = () => {
    const sessionData = sessionStorage.getItem("userData");
    const localData = localStorage.getItem("userData");
    const dataStr = sessionData || localData;

    try {
      return dataStr ? JSON.parse(dataStr) : null;
    } catch (e) {
      console.error("Error parsing userData:", e);
      return null;
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return localStorage.getItem("role") === "ROLE_ADMIN";
  };

  // Initialize login state
  const [isLoggedIn, setIsLoggedIn] = useState(!!getToken());
  const [userData, setUserData] = useState(getUserData());

  const userDropdownRef = useRef(null);
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const isAdminPage = location.pathname.startsWith('/admin');

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check login status and update state on component mount and route changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = getToken();
      const user = getUserData();

      console.log("🔍 Checking login status:");
      console.log("Token found:", !!token);
      console.log("UserData found:", !!user);
      console.log("User name:", user?.name);

      setIsLoggedIn(!!token);
      setUserData(user);
    };

    checkLoginStatus();
  }, [location.pathname]); // Re-check when route changes

  // Listen for custom login/logout events
  useEffect(() => {
    const handleAuthChange = () => {
      const token = getToken();
      const user = getUserData();

      console.log("🔄 Auth change detected");
      console.log("Token found:", !!token);
      console.log("UserData found:", !!user);

      setIsLoggedIn(!!token);
      setUserData(user);
    };

    // Listen for custom events
    window.addEventListener("authChange", handleAuthChange);

    // Also listen for storage events (in case of changes in other tabs)
    window.addEventListener("storage", handleAuthChange);

    return () => {
      window.removeEventListener("authChange", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  const handleMarketplaceClick = (e) => {
    e.preventDefault();
    if (isHomePage) {
      const productsSection = document.getElementById('products');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/', { state: { scrollToProducts: true } });
    }
    setIsMobileMenuOpen(false);
  };

  const handleCalculatorClick = (path) => {
    setIsDropdownOpen(false);
    setIsMobileDropdownOpen(false);
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsMobileDropdownOpen(false);
  };

  const toggleMobileDropdown = () => {
    setIsMobileDropdownOpen(!isMobileDropdownOpen);
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  // Logout handler - clear both storages
  const handleLogout = () => {
    // Clear sessionStorage
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("loginId");

    // Clear localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    localStorage.removeItem("loginId");
    localStorage.removeItem("role");

    setIsLoggedIn(false);
    setUserData(null);
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);

    // Dispatch custom event for other components
    window.dispatchEvent(new Event("authChange"));

    console.log("🚪 Logged out successfully");
    navigate("/login");
  };

  const handleDropdownNavigation = (path) => {
    setIsUserDropdownOpen(false);
    navigate(path);
  };

  const calculatorCategories = [
    {
      title: "Investment & Savings",
      icon: "💰",
      calculators: [
        { name: "SIP Calculator", path: "/finance/sip" },
        { name: "Mutual Fund Calculator", path: "/finance/mutual" },
        { name: "Compound Interest", path: "/finance/compound" },
        { name: "Simple Interest", path: "/finance/simple" },
        { name: "Fixed Deposit (FD)", path: "/finance/fd" },
        { name: "Recurring Deposit (RD)", path: "/finance/rd" },
        { name: "PPF Calculator", path: "/finance/ppf" },
        { name: "NPS Calculator", path: "/finance/nps" },
        { name: "SSY Calculator", path: "/finance/ssy" },
        { name: "Retirement Corpus", path: "/finance/retirement" },
        { name: "Goal-Based Investment", path: "/finance/goal" },
      ]
    },
    {
      title: "Loans & Credit",
      icon: "🏠",
      calculators: [
        { name: "EMI Calculator", path: "/finance/emi" },
        { name: "Loan Amortization", path: "/finance/loan" },
        { name: "Loan Eligibility", path: "/finance/loanEligibility" },
        { name: "Prepayment Impact", path: "/finance/prepayment" },
        { name: "Balance Transfer", path: "/finance/balance" },
        { name: "Credit Card Interest", path: "/finance/credit" },
        { name: "Debt-to-Income Ratio", path: "/finance/debt" },
      ]
    },
    {
      title: "Business & ROI",
      icon: "📈",
      calculators: [
        { name: "ROI Calculator", path: "/finance/roi" },
        { name: "Break-Even Point", path: "/finance/break" },
        { name: "Profit Margin", path: "/finance/profit" },
        { name: "NPV Calculator", path: "/finance/npv" },
        { name: "IRR Calculator", path: "/finance/irr" },
        { name: "DCF Calculator", path: "/finance/dcf" },
      ]
    },
    {
      title: "Taxation",
      icon: "🧾",
      calculators: [
        { name: "Income Tax", path: "/finance/income" },
        { name: "GST Calculator", path: "/finance/GST" },
        { name: "HRA Exemption", path: "/finance/hra" },
        { name: "Gratuity Calculator", path: "/finance/gratuity" },
        { name: "Provident Fund (PF)", path: "/finance/pf" },
        { name: "TDS Calculator", path: "/finance/tds" },
        { name: "Capital Gains Tax", path: "/finance/capital" },
      ]
    },
    {
      title: "Daily Life & Personal",
      icon: "🧮",
      calculators: [
        { name: "Unit Converter", path: "/finance/unit" },
        { name: "Tip Calculator", path: "/finance/tip" },
        { name: "Discount Calculator", path: "/finance/discount" },
        { name: "Percentage Calculator", path: "/finance/percentage" },
        { name: "Age Calculator", path: "/finance/age" },
        { name: "Date Difference", path: "/finance/date" },
        { name: "Salary Breakup", path: "/finance/salary" },
        { name: "Currency Converter", path: "/finance/currency" },
      ]
    },
    {
      title: "Health & Fitness",
      icon: "🏥",
      calculators: [
        { name: "BMI Calculator", path: "/finance/bmi" },
        { name: "BMR Calculator", path: "/finance/bmr" },
        { name: "Calorie Intake", path: "/finance/calorie" },
        { name: "Ideal Weight", path: "/finance/ideal" },
        { name: "Body Fat %", path: "/finance/body" },
        { name: "Heart Rate Zone", path: "/finance/heart" },
        { name: "Pregnancy Due Date", path: "/finance/pregnancy" },
      ]
    },
    {
      title: "Business & Accounting",
      icon: "💼",
      calculators: [
        { name: "Invoice Calculator", path: "/finance/invoice" },
        { name: "Inventory Valuation", path: "/finance/inventory" },
        { name: "Depreciation", path: "/finance/depreciation" },
        { name: "Gross Profit / Net Profit", path: "/finance/gross" },
        { name: "Loan vs Lease", path: "/finance/loanvslease" },
        { name: "CAGR Calculator", path: "/finance/cagr" },
        { name: "EBITDA Calculator", path: "/finance/ebitda" },
      ]
    },
    {
      title: "Technical & Utility",
      icon: "🧰",
      calculators: [
        { name: "Percentage Change", path: "/finance/PercentageChange" },
        { name: "Fuel Cost Calculator", path: "/finance/fuel" },
        { name: "Electricity Bill Estimator", path: "/finance/electricity" },
        { name: "Time Duration", path: "/finance/time" },
        { name: "Savings Goal Timeline", path: "/finance/savings" },
        { name: "Currency Inflation Adjuster", path: "/finance/inflation" },
      ]
    },
  ];

  // Debug render
  console.log("🎨 Navbar rendering - isLoggedIn:", isLoggedIn, "userData:", userData?.name);

  // Determine which toggle button to show
  const showCalculatorToggle = toggleSidebar && !isAdminPage;
  const showAdminToggle = toggleAdminSidebar && isAdminPage && isAdmin();

  return (
    <nav className="custom-navbar">
      <div className="navbar-container">
        {/* Left: Hamburger + Logo + Links */}
        <div className="navbar-left">
          {/* Calculator Sidebar Toggle (for calculator pages) */}
          {showCalculatorToggle && (
            <button
              onClick={toggleSidebar}
              className="btn btn-link text-light p-0"
              aria-label="Toggle Calculator Sidebar"
              title="Toggle Calculator Sidebar"
            >
              <Menu size={24} />
            </button>
          )}

          {/* Admin Sidebar Toggle (for admin pages only) */}
          {showAdminToggle && (
            <button
              onClick={toggleAdminSidebar}
              className="btn btn-link text-light p-0"
              aria-label="Toggle Admin Sidebar"
              title="Toggle Admin Sidebar"
            >
              <Menu size={24} />
            </button>
          )}

          <Link to="/" className="navbar-logo">
            <img src="/logo1.png" alt="Company Logo" className="company-logo" />
          </Link>

          {/* Desktop Links */}
          <ul className={`navbar-links ${isMobileMenuOpen ? 'mobile-menu-active' : ''}`}>
            <li>
              <a href="#products" onClick={handleMarketplaceClick} className="nav-link">
                Marketplace
              </a>
            </li>

            <li
              className={`nav-dropdown-container ${isMobileDropdownOpen ? 'mobile-dropdown-active' : ''}`}
              onMouseEnter={() => isHomePage && window.innerWidth > 991 && setIsDropdownOpen(true)}
              onMouseLeave={() => window.innerWidth > 991 && setIsDropdownOpen(false)}
            >
              {isHomePage ? (
                <div className="nav-link-with-dropdown">
                  <span
                    className="nav-link"
                    onClick={() => window.innerWidth <= 991 && toggleMobileDropdown()}
                  >
                    Financial Calculators
                    <ChevronDown size={16} className="dropdown-icon" />
                  </span>

                  {((isDropdownOpen && window.innerWidth > 991) || (isMobileDropdownOpen && window.innerWidth <= 991)) && (
                    <div className="mega-dropdown">
                      <div className="dropdown-grid">
                        {calculatorCategories.map((category, idx) => (
                          <div key={idx} className="dropdown-column">
                            <div className="dropdown-category-title">
                              <span className="category-icon">{category.icon}</span>
                              {category.title}
                            </div>
                            <ul className="dropdown-items">
                              {category.calculators.map((calc, calcIdx) => (
                                <li key={calcIdx}>
                                  <button
                                    onClick={() => handleCalculatorClick(calc.path)}
                                    className="dropdown-item-btn"
                                  >
                                    {calc.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/finance/sip"
                  className="nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Financial Calculators
                </Link>
              )}
            </li>
          </ul>
        </div>

        {/* Right: User Account Dropdown */}
        <div className="navbar-right">
          {/* BEFORE LOGIN UI */}
          {!isLoggedIn && (
            <div className="auth-buttons d-flex align-items-center gap-2">
              <Link to="/login" className="btn btn-outline-light">
                Login
              </Link>
              <Link to="/signup" className="btn btn-light">
                Sign Up
              </Link>
            </div>
          )}

          {/* AFTER LOGIN UI */}
          {isLoggedIn && (
            <div className="user-account-dropdown" ref={userDropdownRef}>
              <button
                className="user-account-trigger"
                onClick={toggleUserDropdown}
                aria-expanded={isUserDropdownOpen}
              >
                <div className="user-avatar">
                  <User size={18} />
                </div>
                <span className="user-greeting">
                  {userData?.name || "Account"}
                </span>
                <ChevronDown size={16} />
              </button>

              {isUserDropdownOpen && (
                <div className="user-account-menu">
                  <button
                    className="user-menu-item"
                    onClick={() => handleDropdownNavigation("/")}
                  >
                    🏠 Home
                  </button>

                  <button
                    className="user-menu-item"
                    onClick={() => handleDropdownNavigation("/profile")}
                  >
                    👤 My Profile
                  </button>

                  {/* Show Dashboard link only for ADMIN users */}
                  {isAdmin() && (
                    <button
                      className="user-menu-item"
                      onClick={() => handleDropdownNavigation("/admin/dashboard")}
                    >
                      📊 Admin Dashboard
                    </button>
                  )}

                  <button
                    className="user-menu-item logout-item"
                    onClick={handleLogout}
                  >
                    ⏻ Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;